<script lang="ts">
    import { onDestroy, setContext } from 'svelte'
    import { derived as sderived, writable, type Writable } from 'svelte/store'

    import { SECTION_TYPES } from '../Sections/sections'
    import { cloneDeep } from 'lodash-es'
    import * as yup from 'yup'

    import type { CrumbArray } from 'hydria/components/breadcrumb'
    import { validateSyncAt } from 'hydria/document_helpers'
    import { HydriaDocument } from 'hydria/HydriaDocument'
    import { gettext as _, tgettext, type TGettext } from 'hydria/services/gettext'
    import { supportedLanguages } from 'hydria/services/language'
    import { groups as ugroups } from 'hydria/services/session'
    import { urltitle } from 'hydria/utils/strings'

    import { Breadcrumb } from 'breadcrumb'

    import type { ILocalDocument, ILocalRevision } from './pageDefs'

    import DocumentEdit from 'hydria/components/DocumentEdit.svelte'
    import GenTextInput from 'hydria/components/Generics/GenTextInput.svelte'
    import GenUrlTitleInput from 'hydria/components/Generics/GenUrlTitleInput.svelte'
    import LanguageSelector from 'hydria/components/Generics/LanguageSelector.svelte'
    import IntroHtml from 'hydria/field_components/IntroHtml.svelte'
    import MetaDesc from '../Sections/BaseFields/MetaDesc.svelte'
    import AddSection from '../Sections/Types/AddSection.svelte'
    import PageSection from '../Sections/Types/PageSection.svelte'
    import { spath } from 'hydria/svelte_helpers'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    interface Props {
        route: any
    }

    let { route }: Props = $props()
    const revFields = ['id', 'title', 'url_title', 'url_code', 'meta_desc', 'intro_html', 'sections', 'lg']

    const revSchema = yup.object().shape({
        lg: yup
            .string()
            .required()
            .test('lgIsValid', '${path} is not valid', (value) => supportedLanguages.includes(value)),
        title: yup.string().required(),
        url_title: yup
            .string()
            .required()
            .test('url_titleIsValid', '${path} is not valid', (value: string) => urltitle(value) === value),
        url_code: yup.string().optional().nullable(),
        meta_desc: yup.string(),
        intro_html: yup.string().required(),
    })

    class LocalDocument extends HydriaDocument {
        docType = 'page'
        docTypePlural = 'pages'

        docTypeName = tgettext('Page')
        docTypeNamePlural = tgettext('Pages')

        page_type?: 'home' | 'generic'
    }

    const Document = new LocalDocument()
    const status = sderived(Document.status, ($status) => $status)

    let inDocId = $derived(route.result.path.params?.docId)
    let inRevId = $derived(route.result.path.params?.revId)

    const docSaved = sderived(Document.document, ($doc) => $doc as ILocalDocument)
    const revSaved = sderived(Document.revision, ($rev) => (!$rev ? $rev : Object.fromEntries(Object.entries($rev).filter(([k, v]) => revFields.includes(k)))) as ILocalRevision)
    let revEdit: Writable<ILocalRevision | undefined> = writable(undefined)

    setContext('revEdit', revEdit)
    setContext('Document', Document)
    setContext('attachments', Document.attachedFiles)

    let formIsValid = $derived(revSchema.isValidSync($revEdit))

    unsubs.push(
        revEdit.subscribe(($revEdit) => {
            if ($status == 'ready' && $revEdit && $revEdit.sections == null) $revEdit.sections = []
        }),
    )

    const crumbs = sderived(status, ($status) => {
        const c: CrumbArray = [{ txt: Document.docTypeNamePlural, url: spath(`/${Document.docTypePlural.replace('_', '-')}`) }]
        switch ($status) {
            case 'loading':
                c.push({
                    txt: '…',
                })
                break
            case 'error':
            case 'not-found':
                c.push({
                    icon: 'mdi:alert',
                })
                break
            case 'ready':
                c.push({
                    txt: tgettext('Edit'),
                })
                c.push({
                    txt: `<span class="text-muted">${Document.docId}</span>`,
                    html: true,
                })
        }
        return c
    })

    let introHelpText: TGettext | 'default' = $state('default')

    unsubs.push(
        docSaved.subscribe(($docSaved) => {
            if ($docSaved && $docSaved.page_type == 'home') introHelpText = tgettext('Affiché avec le bloc de connexion/perte de mot de passe.')
        }),
    )
</script>

<Breadcrumb {crumbs} />

<DocumentEdit {Document} docId={inDocId} revId={inRevId} {formIsValid} {revFields}>
    {#if $revEdit}
        <div class="card">
            <div class="card-header text-bg-primary">
                {#if $docSaved.page_type == 'home'}{$_('Accueil')}
                {:else}{$_('Document')}
                {/if}&#x2009;: {$_('Méta données')}
            </div>
            <div class="card-body d-grid gap-2">
                <GenTextInput label={tgettext('Titre')} saved={$revSaved.title} bind:value={$revEdit.title} isValid={(v) => validateSyncAt(revSchema, 'title', { title: v })} helpText={tgettext('Ce texte est utilisé pour le titre de la page, dans la barre du navigateur.')} />
                {#if $docSaved.page_type != 'home' || $ugroups.has('dev')}
                    <GenUrlTitleInput label={tgettext('Titre URL')} title={$revEdit.title} saved={$revSaved.url_title} bind:value={$revEdit.url_title} isValid={(v) => validateSyncAt(revSchema, 'url_title', { url_title: v })} urlTitle={Document.urlTitle} lg={$revEdit.lg} />
                {/if}

                {#if $docSaved.page_type != 'home'}
                    <GenUrlTitleInput label={tgettext('Code interne')} title={undefined} saved={$revSaved.url_code} bind:value={$revEdit.url_code} isValid={(v) => validateSyncAt(revSchema, 'url_code', { url_code: v })} lg={$revEdit.lg} helpText={tgettext('Ceci est utilisé à l’interne pour retouver cette page et y pointer dans les liens statiques. Changer la valeur peut causer le site entier à tomber en erreur 500.')} />
                {/if}

                <LanguageSelector bind:value={$revEdit.lg} />

                <MetaDesc bind:saved={$revSaved.meta_desc} bind:value={$revEdit.meta_desc} disabled={$status != 'ready'} isValid={(v) => validateSyncAt(revSchema, 'meta_desc', { meta_desc: v })} />
            </div>
        </div>

        <div class="card">
            <div class="card-header text-bg-primary">{$_('Base')}</div>
            <div class="card-body">
                <IntroHtml
                    saved={$revSaved.intro_html}
                    bind:value={$revEdit.intro_html}
                    disabled={$status != 'ready'}
                    helpText={introHelpText}
                    isValid={(v) => {
                        if (v == null) return false
                        const clean = v.replaceAll(/<p><\/p>/gi, '')
                        return clean != ''
                    }}
                />
            </div>
        </div>

        <AddSection {revEdit} {docSaved} />

        {#each $revEdit.sections as section, si (section.id)}
            <PageSection
                bind:sectionEdit={
                    () => $revEdit.sections[si],
                    (ns) => {
                        $revEdit.sections[si] = ns
                    }
                }
                sectionSaved={$revSaved.sections.filter((s) => s.id == section.id)[0]}
                isFirst={si < 1}
                isLast={si >= $revEdit.sections.length - 1}
                disabled={$status != 'ready'}
                remove={() => {
                    revEdit.update(($re) => {
                        $re.sections = $re.sections.filter((s) => s.id !== section.id)
                        return $re
                    })
                }}
                moveUp={() => {
                    if (si < 1) return
                    revEdit.update(($revEdit) => {
                        $revEdit.sections.splice(si - 1, 0, $revEdit.sections.splice(si, 1)[0])
                        return $revEdit
                    })
                }}
                moveDown={() => {
                    if (si >= $revEdit.sections.length - 1) return
                    revEdit.update(($revEdit) => {
                        $revEdit.sections.splice(si + 1, 0, $revEdit.sections.splice(si, 1)[0])
                        return $revEdit
                    })
                }}
            />

            <AddSection {revEdit} {docSaved} afterSectionId={section.id} />
        {/each}
    {/if}
</DocumentEdit>
